import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Line, Doughnut } from 'react-chartjs-2';
import '../../../pages/analytics/chartConfig';
import JalaliDatePicker from '../../common/JalaliDatePicker';
import { getPlatformProfit, PlatformProfitPeriod, PlatformProfitReport, PlatformSellerRow } from './profitApi';
import './PlatformProfit.css';

const PERIODS: Array<{ key: PlatformProfitPeriod; label: string }> = [
  { key: 'today', label: 'امروز' },
  { key: '7d', label: '۷ روز' },
  { key: '30d', label: '۳۰ روز' },
  { key: 'month', label: 'این ماه' },
  { key: 'last_month', label: 'ماه قبل' },
  { key: 'year', label: 'امسال' },
  { key: 'all', label: 'از ابتدا' },
  { key: 'custom', label: 'دلخواه' },
];

const C = {
  revenue: '#C25E7E',
  cost: '#8C6F7C',
  profit: '#2E7D5B',
  deep: '#B25373',
  blush: '#F8D7DF',
};

const toFa = (s: string) => s.replace(/[0-9]/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[Number(d)]);
const faNum = (v: unknown) => toFa(Math.round(Number(v) || 0).toLocaleString('en-US')).replace(/,/g, '٬');
const faToman = (v: unknown) => `${faNum(v)} تومان`;
const faPct = (v: number | null | undefined) =>
  v === null || v === undefined || !Number.isFinite(v) ? '—' : `${toFa(String(Math.round(v * 10) / 10).replace('.', '٫'))}٪`;
const faCompact = (v: number) => {
  const a = Math.abs(v);
  const s = v < 0 ? '−' : '';
  const f = (x: number, u: string) => `${s}${toFa(String(Math.round(x * 10) / 10).replace('.', '٫'))} ${u}`;
  if (a >= 1e9) return f(a / 1e9, 'میلیارد');
  if (a >= 1e6) return f(a / 1e6, 'میلیون');
  if (a >= 1e3) return f(a / 1e3, 'هزار');
  return `${s}${toFa(String(Math.round(a)))}`;
};

const Trend: React.FC<{ v?: number | null; invert?: boolean; pp?: boolean }> = ({ v, invert, pp }) => {
  if (v === undefined || v === null || !Number.isFinite(v)) return null;
  if (v === 0) return <span className="pp-trend flat">بدون تغییر</span>;
  const up = v > 0;
  const good = invert ? !up : up;
  return (
    <span className={`pp-trend ${good ? 'good' : 'bad'}`} title="نسبت به دورهٔ قبل">
      {up ? '▲' : '▼'} {pp ? `${faPct(Math.abs(v)).replace('٪', '')} واحد` : faPct(Math.abs(v))}
    </span>
  );
};

const Skel: React.FC<{ h?: number; w?: string }> = ({ h = 18, w = '100%' }) => (
  <div className="pp-skel" style={{ height: h, width: w }} />
);

const SellersTable: React.FC<{ rows: PlatformSellerRow[]; mode: 'revenue' | 'profit' }> = ({ rows, mode }) =>
  rows.length === 0 ? (
    <div className="pp-empty">{mode === 'profit' ? 'هنوز فروشی با قیمت خرید ثبت نشده' : 'هنوز فروشی ثبت نشده'}</div>
  ) : (
    <div className="pp-table-wrap">
      <table className="pp-table">
        <thead>
          <tr>
            <th>#</th>
            <th>فروشگاه</th>
            <th>فروش</th>
            <th>سود ناخالص</th>
            <th>حاشیه</th>
            <th>پوشش</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.storeId}>
              <td className="pp-muted">{faNum(i + 1)}</td>
              <td className="name">{r.storeName}</td>
              <td style={{ fontWeight: mode === 'revenue' ? 800 : 500 }}>{faToman(r.revenue)}</td>
              <td className={r.grossProfit === null ? 'pp-muted' : r.grossProfit < 0 ? 'pp-bad' : 'pp-good'}>
                {r.grossProfit === null ? 'نامشخص' : faToman(r.grossProfit)}
              </td>
              <td className={r.grossMargin !== null && r.grossMargin < 0 ? 'pp-bad' : ''}>{faPct(r.grossMargin)}</td>
              <td className="pp-muted">{faPct(r.coverage)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

/**
 * 💄 سود و زیان پلتفرم — GMV، کارمزد پلتفرم، سود ناخالص فروشندگان (جایی که قیمت خرید ثبت شده)،
 * پوشش قیمت خرید و فروشندگان برتر. بدون مجوز admin_dashboard_profit_read کلاً پنهان می‌شود.
 */
const PlatformProfitSection: React.FC = () => {
  const [period, setPeriod] = useState<PlatformProfitPeriod>('30d');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [data, setData] = useState<PlatformProfitReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const reqId = useRef(0);

  const load = useCallback(async () => {
    if (period === 'custom' && (!customFrom || !customTo)) return;
    const id = ++reqId.current;
    setLoading(true);
    setError(null);
    try {
      const res = await getPlatformProfit({
        period,
        ...(period === 'custom' ? { from: customFrom, to: customTo } : {}),
      });
      if (id !== reqId.current) return;
      setData(res);
    } catch (e: any) {
      if (id !== reqId.current) return;
      // api.ts خطای 403 را به Error با پیام «...دسترسی لازم...» تبدیل می‌کند (silentForbidden → بدون ریدایرکت)
      const msg = String(e?.message || '');
      if (e?.response?.status === 403 || msg.includes('دسترسی لازم')) setForbidden(true);
      else setError('بارگذاری گزارش سود و زیان ناموفق بود.');
    } finally {
      if (id === reqId.current) setLoading(false);
    }
  }, [period, customFrom, customTo]);

  useEffect(() => {
    load();
  }, [load]);

  const k = data?.kpis;
  const ch = data?.change || {};

  const lineData = useMemo(() => {
    const s = data?.series || [];
    return {
      labels: s.map((p) => p.label),
      datasets: [
        {
          label: 'فروش',
          data: s.map((p) => p.revenue),
          borderColor: C.revenue,
          backgroundColor: 'rgba(224,127,152,0.18)',
          fill: true,
          tension: 0.35,
          pointRadius: 0,
          borderWidth: 2,
        },
        {
          label: 'سود ناخالص',
          data: s.map((p) => p.profit),
          borderColor: C.profit,
          backgroundColor: 'rgba(46,125,91,0.12)',
          fill: true,
          tension: 0.35,
          pointRadius: 0,
          borderWidth: 2,
        },
        {
          label: 'بهای تمام‌شده',
          data: s.map((p) => p.cost),
          borderColor: C.cost,
          borderDash: [5, 4],
          fill: false,
          tension: 0.35,
          pointRadius: 0,
          borderWidth: 2,
        },
      ],
    };
  }, [data]);

  const lineOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'index' as const, intersect: false },
      plugins: {
        legend: {
          position: 'top' as const,
          rtl: true,
          labels: { font: { family: 'Vazirmatn' }, usePointStyle: true, boxWidth: 8, color: '#4A2A3A' },
        },
        tooltip: {
          rtl: true,
          bodyFont: { family: 'Vazirmatn' },
          titleFont: { family: 'Vazirmatn' },
          callbacks: { label: (ctx: any) => `${ctx.dataset.label}: ${faToman(ctx.parsed.y)}` },
        },
      },
      scales: {
        x: { reverse: true, grid: { display: false }, ticks: { font: { family: 'Vazirmatn' }, color: '#7A5A68', maxTicksLimit: 10 } },
        y: {
          position: 'right' as const,
          grid: { color: '#F3E6EA' },
          ticks: { font: { family: 'Vazirmatn' }, color: '#7A5A68', callback: (v: any) => faCompact(Number(v)) },
        },
      },
    }),
    []
  );

  if (forbidden) return null;

  const known = k?.revenueWithCost || 0;
  const missing = k?.revenueMissingCost || 0;
  const donutEmpty = known + missing <= 0;

  const kpis = [
    { t: 'GMV (ارزش ناخالص فروش)', v: k && faToman(k.gmv), s: k && `${faNum(k.ordersCount)} سفارش`, c: ch.revenue },
    {
      t: 'درآمد کارمزد پلتفرم',
      v: k && faToman(k.platformCommission),
      s: k && `تخمینی · نرخ مؤثر ${faPct(k.commissionRatePercent)}`,
      c: ch.commission,
    },
    { t: 'فروش خالص فروشندگان', v: k && faToman(k.revenue), s: k && `${faNum(k.activeSellers)} فروشندهٔ فعال`, c: ch.revenue },
    {
      t: 'سود ناخالص فروشندگان',
      v: k && faToman(k.grossProfit),
      s: k && `فقط فروشِ دارای قیمت خرید`,
      c: ch.grossProfit,
      hl: true,
    },
    { t: 'حاشیه سود فروشندگان', v: k && faPct(k.grossMargin), s: 'سود ناخالص ÷ فروش', c: ch.grossMargin, pp: true },
    { t: 'کارمزد وصول‌شده (برداشت‌ها)', v: k && faToman(k.withdrawalFeesCollected), s: k && `${faNum(k.withdrawalsCount)} برداشت`, c: undefined },
  ];

  return (
    <section className="pp-section" dir="rtl" aria-label="سود و زیان پلتفرم">
      <div className="pp-head">
        <div style={{ flex: 1, minWidth: 220 }}>
          <h2>سود و زیان پلتفرم</h2>
          <div className="pp-sub">
            {data ? `${data.period.label} · ${data.period.fromJalali} تا ${data.period.toJalali}` : 'در حال محاسبه…'}
          </div>
        </div>
        <button className="dash-refresh" onClick={load} disabled={loading} title="بروزرسانی" aria-label="بروزرسانی">
          ⟳
        </button>
      </div>

      <div className="pp-periods" role="tablist" aria-label="بازهٔ زمانی">
        {PERIODS.map((p) => (
          <button
            key={p.key}
            role="tab"
            aria-selected={period === p.key}
            className={`pp-period ${period === p.key ? 'active' : ''}`}
            onClick={() => setPeriod(p.key)}
          >
            {p.label}
          </button>
        ))}
      </div>

      {period === 'custom' && (
        <div className="pp-custom">
          <span>از</span>
          <div style={{ width: 170 }}>
            <JalaliDatePicker value={customFrom} onChange={setCustomFrom} outputDateOnly placeholder="تاریخ شروع" />
          </div>
          <span>تا</span>
          <div style={{ width: 170 }}>
            <JalaliDatePicker value={customTo} onChange={setCustomTo} outputDateOnly placeholder="تاریخ پایان" />
          </div>
        </div>
      )}

      {error && <div className="pp-alert err">{error}</div>}
      {!!data?.catalog.productsMissingCost && (
        <div className="pp-alert warn">
          قیمت خرید {faNum(data.catalog.productsMissingCost)} محصول از {faNum(data.catalog.activeProducts)} محصول ثبت نشده است (پوشش کاتالوگ{' '}
          {faPct(data.catalog.productsCostCoverage)})؛ سود این محصولات در آمار سود فروشندگان لحاظ نمی‌شود.
        </div>
      )}
      {!!data?.lossMakingCount && (
        <div className="pp-alert err">
          {faNum(data.lossMakingCount)} محصول در این بازه زیر قیمت خرید فروخته شده‌اند:{' '}
          {data.lossMaking
            .slice(0, 3)
            .map((p) => `«${p.title}» (${faToman(p.profit)})`)
            .join('، ')}
        </div>
      )}

      <div className="pp-kpis">
        {kpis.map((x) => (
          <div key={x.t} className={`pp-kpi ${x.hl ? 'hl' : ''}`}>
            <div className="pp-kpi-title">{x.t}</div>
            {loading && !data ? (
              <>
                <Skel h={26} w="75%" />
                <Skel h={12} w="50%" />
              </>
            ) : (
              <>
                <div className="pp-kpi-value">{x.v || '—'}</div>
                <div className="pp-kpi-foot">
                  <Trend v={x.c} pp={x.pp} />
                  {x.s && <span style={{ opacity: 0.85 }}>{x.s}</span>}
                </div>
              </>
            )}
          </div>
        ))}
      </div>

      <div className="pp-grid-2">
        <div className="pp-card">
          <h3>روند فروش، بهای تمام‌شده و سود</h3>
          <div className="pp-sub" style={{ marginBottom: 8 }}>
            {data?.period.granularity === 'day' ? 'روزانه' : data?.period.granularity === 'week' ? 'هفتگی' : 'ماهانه'}
          </div>
          <div style={{ height: 300 }}>
            {loading && !data ? (
              <Skel h={300} />
            ) : k && k.revenue === 0 && k.refunds === 0 ? (
              <div className="pp-empty" style={{ paddingTop: 110 }}>هنوز فروشی در این بازه ثبت نشده</div>
            ) : (
              <Line data={lineData} options={lineOptions as any} />
            )}
          </div>
        </div>
        <div className="pp-card">
          <h3>پوشش قیمت خرید</h3>
          <div className="pp-sub" style={{ marginBottom: 8 }}>سهم فروشی که سودش قابل محاسبه است</div>
          <div style={{ position: 'relative', height: 180 }}>
            <Doughnut
              data={{
                labels: ['دارای قیمت خرید', 'بدون قیمت خرید'],
                datasets: [
                  {
                    data: donutEmpty ? [1] : [known, missing],
                    backgroundColor: donutEmpty ? ['#F1E6EA'] : [C.deep, C.blush],
                    borderWidth: 0,
                  },
                ],
              }}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                cutout: '72%',
                plugins: { legend: { display: false }, tooltip: { enabled: !donutEmpty, rtl: true, callbacks: { label: (c: any) => `${c.label}: ${faToman(c.parsed)}` } } },
              }}
            />
            <div style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', pointerEvents: 'none' }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontWeight: 800, fontSize: '1.3rem' }}>{donutEmpty ? '—' : faPct(k?.costCoverage)}</div>
                <div className="pp-muted" style={{ fontSize: '0.7rem' }}>پوشش</div>
              </div>
            </div>
          </div>
          <div className="pp-legend-row">
            <span className="pp-dot" style={{ background: C.deep }} />
            <span style={{ flex: 1 }}>دارای قیمت خرید</span>
            <b>{faToman(known)}</b>
          </div>
          <div className="pp-legend-row">
            <span className="pp-dot" style={{ background: C.blush, border: '1px solid #E07F98' }} />
            <span style={{ flex: 1 }}>بدون قیمت خرید</span>
            <b>{faToman(missing)}</b>
          </div>
        </div>
      </div>

      <div className="pp-grid-2 even">
        <div className="pp-card">
          <h3>فروشندگان برتر بر اساس فروش</h3>
          {loading && !data ? <Skel h={160} /> : <SellersTable rows={data?.topSellersByRevenue || []} mode="revenue" />}
        </div>
        <div className="pp-card">
          <h3>فروشندگان برتر بر اساس سود</h3>
          {loading && !data ? <Skel h={160} /> : <SellersTable rows={data?.topSellersByProfit || []} mode="profit" />}
        </div>
      </div>

      <div className="pp-grid-2 even">
        <div className="pp-card">
          <h3>پرفروش‌ترین محصولات</h3>
          {loading && !data ? (
            <Skel h={160} />
          ) : !data?.topProductsByRevenue.length ? (
            <div className="pp-empty">هنوز فروشی ثبت نشده</div>
          ) : (
            <div className="pp-table-wrap">
              <table className="pp-table">
                <thead>
                  <tr>
                    <th>محصول</th>
                    <th>تعداد</th>
                    <th>فروش</th>
                    <th>حاشیه</th>
                  </tr>
                </thead>
                <tbody>
                  {data.topProductsByRevenue.map((p) => (
                    <tr key={p.productId}>
                      <td className="name">{p.title}</td>
                      <td>{faNum(p.units)}</td>
                      <td>{faToman(p.revenue)}</td>
                      <td className={p.margin === null ? 'pp-muted' : p.margin < 0 ? 'pp-bad' : 'pp-good'}>{faPct(p.margin)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        <div className="pp-card">
          <h3>به تفکیک نوع محصول</h3>
          {loading && !data ? (
            <Skel h={160} />
          ) : !data?.categories.length ? (
            <div className="pp-empty">هنوز فروشی ثبت نشده</div>
          ) : (
            <div className="pp-table-wrap">
              <table className="pp-table">
                <thead>
                  <tr>
                    <th>نوع محصول</th>
                    <th>فروش</th>
                    <th>سود ناخالص</th>
                    <th>حاشیه</th>
                  </tr>
                </thead>
                <tbody>
                  {data.categories.slice(0, 10).map((c) => (
                    <tr key={c.id || c.name}>
                      <td className="name">{c.name}</td>
                      <td>{faToman(c.revenue)}</td>
                      <td className={c.profit === null ? 'pp-muted' : c.profit < 0 ? 'pp-bad' : 'pp-good'}>
                        {c.profit === null ? 'نامشخص' : faToman(c.profit)}
                      </td>
                      <td>{faPct(c.margin)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <div className="pp-note">
        فروش = سفارش‌های پرداخت‌شده‌ای که لغو/مسترد نشده‌اند، پس از تخفیف‌ها و کسر مرجوعی. GMV شامل هزینهٔ ارسال و اقلام بعداً
        مستردشده است. سود ناخالص فقط برای اقلامی محاسبه می‌شود که قیمت خرید آن‌ها لحظهٔ سفارش ثبت شده است. کارمزد پلتفرم
        طبق طرح کارمزد هر فروشگاه تخمین زده می‌شود (طرح‌های مبلغ‌ثابت: کارمزد واقعی برداشت‌های همان بازه).
      </div>
    </section>
  );
};

export default PlatformProfitSection;
